/**
 * Schedule View — the appointment stripe is the provider's Open Dental colour.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). The one collaborator it needs,
 * layout.js `appointmentProviderNum`, is passed in as a dep.
 *
 * ── THE RULE (Nilay, 2026-10-23) ──────────────────────────────────────────────────────────────
 * The left stripe of an appointment block / phone card is `provider.ProvColor` — the "Appointment
 * Color" the office picked for that provider in Open Dental — on a white block, the Practice Pulse
 * treatment. Columns are white; nothing on the page is coloured by operatory any more.
 *
 * ── WHICH PROVIDER ────────────────────────────────────────────────────────────────────────────
 * The hygienist on a hygiene visit, otherwise the dentist: `appointmentProviderNum`, the same rule
 * Open Dental paints by (and the same one the phone card and the table row already use to NAME the
 * provider, so the colour and the name can never point at two different people).
 *
 * ── THE FORMAT ────────────────────────────────────────────────────────────────────────────────
 * Open Dental stores `ProvColor` as a SIGNED 32-bit ARGB int (.NET Color.ToArgb()): -16711681 is
 * 0xFF00FFFF, cyan. Practice Pulse converts it in `Ppmixin.numToHex` with `num >>>= 0` and byte
 * masks; this is that conversion, ported, minus the alpha byte (every OD colour is opaque, and an
 * 8-digit hex would not be a colour a stripe could use anyway).
 *
 * ── THE FALLBACK (a stripe is never missing, and never invisible) ────────────────────────────
 * Grey `#9ca4a2` ($sv-ink-4) when: the appointment has no provider, the provider is not in the
 * roster, the colour is unset (0 / null — an OLDER deploy of schedule-view-service does not send
 * `ProvColor` at all, and the page must still render), or the colour is too light to see on a white
 * block (white, pale yellow). Offices pick garish colours; a stripe is the only place they appear.
 */

/**
 * Neutral grey (`$sv-ink-4`), the stripe when there is no usable provider colour. ⛔ Moves with
 * `$sv-ink-4` in _tokens.scss (it became the Forms Settings #9ca4a2 in WP-C, 2026-10-23); the
 * prov-color node-test pins the two together, and the block / card / hover-card `accent` defaults.
 */
export const STRIPE_FALLBACK = '#9ca4a2';

/** Relative luminance above which a colour is too pale to read as a stripe on a white block. */
export const TOO_LIGHT_LUMINANCE = 0.85;

/**
 * Open Dental's signed ARGB int -> '#rrggbb', or null when there is no colour to show.
 *
 * Accepts a number or a numeric string (MySQL drivers return strings for some column types). Null
 * for 0 (OD's "unset"), null, undefined, '' and anything that is not a finite number.
 */
export function odColorToHex(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  const argb = n >>> 0; // the unsigned 32-bit pattern of the signed int
  const channel = shift => ((argb >>> shift) & 0xFF).toString(16).padStart(2, '0');
  return `#${channel(16)}${channel(8)}${channel(0)}`;
}

/** WCAG relative luminance of '#rrggbb' (0 black … 1 white); NaN for anything else. */
export function relativeLuminance(hex) {
  if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex)) return NaN;
  const lin = (offset) => {
    const c = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(1) + 0.7152 * lin(3) + 0.0722 * lin(5);
}

/** True when the colour would vanish on a white block. Junk is "not too light": it never throws. */
export function isTooLight(hex) {
  return relativeLuminance(hex) > TOO_LIGHT_LUMINANCE;
}

/** ProvNum -> provider, from the day's `providers[]`. Total: no list is an empty index. */
export function indexProviders(providers) {
  const idx = {};
  (providers || []).forEach((p) => {
    if (!p) return;
    const n = Number(p.ProvNum);
    if (Number.isFinite(n) && p.ProvNum !== null && p.ProvNum !== undefined && p.ProvNum !== '') idx[n] = p;
  });
  return idx;
}

/**
 * One PROVIDER's colour — what their visits' stripe is, for a place that draws the provider rather
 * than a visit (the grid header's chip dot, the table's avatar ring). The same rules as the stripe:
 * Open Dental's colour, or the neutral grey when there is none or it is too pale to see.
 *
 * @param {Object} provider  one of the day's `providers[]` ({ ProvColor, … }), or anything
 * @param {string} [fallback]  defaults to STRIPE_FALLBACK
 */
export function providerColorHex(provider, fallback) {
  const hex = provider && typeof provider === 'object' ? odColorToHex(provider.ProvColor) : null;
  if (!hex || isTooLight(hex)) return fallback || STRIPE_FALLBACK;
  return hex;
}

/**
 * The stripe colour for one appointment.
 *
 * @param {Object} appt            one appointment from /schedule-view/day
 * @param {Object} providersByNum  indexProviders() of the day's providers (the UNFILTERED day's)
 * @param {Object} deps            { appointmentProviderNum, fallback? } — layout.js passes the rule in
 */
export function providerStripeColor(appt, providersByNum, deps) {
  const d = deps || {};
  const fallback = d.fallback || STRIPE_FALLBACK;
  if (!appt || !providersByNum || typeof d.appointmentProviderNum !== 'function') return fallback;
  const provNum = d.appointmentProviderNum(appt);
  if (provNum === null || provNum === undefined) return fallback;
  const provider = providersByNum[provNum];
  if (!provider) return fallback;
  return providerColorHex(provider, fallback);
}
